import { useQuery, useMutation } from '@tanstack/react-query'
import { fromZonedTime } from 'date-fns-tz'
import client from '@/ports/api-client'
import {
  purchasePackage,
  addAddOn,
  purchasePackageWithCoupon,
  addAddOnWithCoupon
} from '@/models/plans/apis'
import queryClient from '@/components/query-client'
import { getLocalizedDateAs } from '@/pages/Listing/util'
import useCurrentSession from './use-current-session'
import useRole from './use-role'

type markPromotionProps = {
  promotionId?: number
  userId?: string
  onSuccess: () => void
  onError: (error: any) => void
}

export type Promotion = {
  promotion_id: number
  title: string
  description: string
  tag: string
  read_state: 'not_read' | 'read'
  applied_datetime: string
  start_datetime: string
}

export const getQueryString = (couponCode?: string | null, addon?: boolean) => {
  if (couponCode && addon) {
    return `?couponCode=${couponCode}&addons=true`
  } else if (couponCode) {
    return `?couponCode=${couponCode}`
  } else if (addon) {
    return '?addons=true'
  }
  return ''
}

const STATIC_PROMOTION = ['LIMITED_TIME_OFFER']

const getPromotionDetails = async ({
  country,
  province,
  promotionId,
  couponCode,
  addon
}: {
  country: string
  province: string
  promotionId: string
  couponCode?: string | null | undefined
  addon?: boolean
}) => {
  const query = getQueryString(couponCode, addon)
  try {
    return await client.get(
      `/catalogs/user/packages/${country}/${province}/${promotionId}${query}`,
      {
        noLoading: true
      }
    )
  } catch (error) {
    throw error
  }
}

const useGetAddOnsSummary = () => {
  return async ({
    items,
    metadata,
    couponCode,
    country,
    state
  }: {
    items: any
    metadata?: any
    couponCode?: string | null | undefined
    country: string
    state: string
  }) => {
    if (!!couponCode) {
      const req = await addAddOnWithCoupon(
        {
          country_code: country,
          state,
          items,
          couponCode,
          metadata
        },
        { couponCode }
      )

      return req
    } else {
      const req = await addAddOn({
        country_code: country,
        state,
        items,
        metadata
      })
      return req
    }
  }
}

const useGetPromotions = ({
  country,
  province,
  promotionId,
  couponCode,
  addon
}: {
  country: string
  province: string
  promotionId: string
  couponCode?: string | null
  addon?: boolean | undefined
}) => {
  const role = useRole()
  const { data, isLoading, error } = useQuery({
    queryKey: ['promotions', province, promotionId, couponCode, addon],
    queryFn: () =>
      getPromotionDetails({
        country,
        province,
        promotionId,
        ...{ couponCode: couponCode ?? null },
        addon: addon
      }),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60000,
    enabled: !!country && !!province && !!promotionId && role === 'landlord'
  })
  return { data, isLoading, error }
}

const convertUTCtoLocal = (utcTimeStr: string) => {
  const date = fromZonedTime(utcTimeStr, 'UTC')
  return getLocalizedDateAs(date, 'yyyy-MM-dd HH:mm:ss')
}

const getLandlordPromotions = async () => {
  try {
    const data: Promotion[] = await client.get(`/promotions`, {
      noLoading: true
    })

    const activatedPromotions = data.filter(
      (promotion: { applied_datetime: string }) => !!promotion.applied_datetime
    )

    const sortedData = activatedPromotions.sort((a, b) => {
      const dateA = new Date(a.applied_datetime).getTime()
      const dateB = new Date(b.applied_datetime).getTime()
      return dateB - dateA
    })

    const formattedPromotions = sortedData.map((promotion: Promotion) => ({
      ...promotion,
      applied_datetime: convertUTCtoLocal(promotion.applied_datetime),
      start_datetime: convertUTCtoLocal(promotion.start_datetime)
    }))

    const staticPromotions = data.filter(promotion => STATIC_PROMOTION.includes(promotion.tag))

    return [...staticPromotions, ...formattedPromotions]
  } catch (error) {
    throw error
  }
}

const markPromotion = async (promotionId: number) => {
  const formData = new FormData()
  formData.append('promotion_id', String(promotionId))
  try {
    return await client.post(`/promotions/read`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  } catch (error) {
    throw error
  }
}

const markAllPromotionsAsRead = async (data: Promotion[]) => {
  const unreadPromotions = data.filter(promotion => promotion.read_state === 'not_read')
  const promotions = unreadPromotions.map(promotion => markPromotion(promotion.promotion_id))

  return await Promise.all(promotions)
}

const useMarkPromotionNotification = () => {
  const session = useCurrentSession()
  const userId = session.id

  const markSinglePromotion = useMutation({
    mutationFn: ({ promotionId }: markPromotionProps) => {
      return markPromotion(promotionId as number)
    },
    onSuccess: async (res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['promotion-notification', userId] })
      variables.onSuccess()
    },
    onError: (error, variables) => {
      variables.onError(error.message)
    }
  })

  const markAllPromotions = useMutation({
    mutationFn: () => {
      const data = queryClient.getQueryData(['promotion-notification', userId]) as Promotion[]
      return markAllPromotionsAsRead(data)
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['promotion-notification', userId] })
    },
    onError: (error, variables: { onError: (error: any) => void }) => {
      variables.onError(error.message)
    }
  })

  return { markSinglePromotion, markAllPromotions }
}

const useGetPromotionNotifications = () => {
  const session = useCurrentSession()
  const userId = session.id
  const queries = useQuery({
    queryKey: ['promotion-notification', userId],
    queryFn: () => getLandlordPromotions(),
    staleTime: 5 * 60000
  })
  return queries
}

const usePurchasePromotion = () => {
  const sponsorListing = useMutation({
    mutationFn: ({
      paymentMethodId,
      items,
      metadata,
      couponCode
    }: {
      paymentMethodId: string
      items: { quantity: number; priceId: string }[]
      metadata: { pLink: string }
      couponCode?: string | null | undefined
    }) => {
      const purchasePackageCaller = couponCode ? purchasePackageWithCoupon : purchasePackage
      return purchasePackageCaller(
        {
          payment_method_id: paymentMethodId,
          items,
          metadata
        },
        { couponCode }
      )
    },
    onSuccess: async data => {}
  })

  return sponsorListing
}

export default useGetPromotions

export {
  usePurchasePromotion,
  useGetAddOnsSummary,
  useGetPromotionNotifications,
  useMarkPromotionNotification
}
